import { motion } from "framer-motion";
import {
  Code,
  Database,
  ExternalLink,
  Globe,
  MapPin,
  Radio,
} from "lucide-react";
import { TechCard } from "../components/tech";
import {
  EXTERNAL_RESOURCES,
  RESOURCE_CATEGORIES,
  Resource,
} from "../data/resources";

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const containerVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const categoryIcons: Record<Resource["category"], React.ReactNode> = {
  reflector: <Radio className="w-5 h-5" />,
  database: <Database className="w-5 h-5" />,
  network: <Globe className="w-5 h-5" />,
  project: <Code className="w-5 h-5" />,
  local: <MapPin className="w-5 h-5" />,
};

const categoryColors: Record<Resource["category"], string> = {
  reflector: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  database: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  network: "bg-green-500/20 text-green-400 border-green-500/30",
  project: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  local: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
};

export default function ResourcesPage() {
  // Group resources by category
  const groupedResources = EXTERNAL_RESOURCES.reduce(
    (acc, resource) => {
      if (!acc[resource.category]) {
        acc[resource.category] = [];
      }
      acc[resource.category].push(resource);
      return acc;
    },
    {} as Record<Resource["category"], Resource[]>,
  );

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.5 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-primary/5 to-transparent" />

        <div className="max-w-6xl mx-auto relative text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-text-primary mb-4">
              Resources
            </h1>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              External resources, reflector registries, and reference materials
              for KQ4TNV repeater users
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {Object.entries(groupedResources).map(([category, resources]) => (
            <motion.div
              key={category}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-12"
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className={`p-2 rounded-lg ${categoryColors[category as Resource["category"]]}`}
                >
                  {categoryIcons[category as Resource["category"]]}
                </div>
                <h2 className="text-xl font-display font-bold text-text-primary">
                  {
                    RESOURCE_CATEGORIES[
                      category as keyof typeof RESOURCE_CATEGORIES
                    ]
                  }
                </h2>
                <span className="text-text-secondary text-sm">
                  ({resources.length}{" "}
                  {resources.length === 1 ? "resource" : "resources"})
                </span>
              </div>

              {/* Resources Grid */}
              <motion.div
                variants={containerVariants}
                initial="initial"
                animate="animate"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {resources.map((resource) => (
                  <motion.div key={resource.id} variants={itemVariants}>
                    <ResourceCard resource={resource} />
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}

function ResourceCard({ resource }: { resource: Resource }) {
  return (
    <TechCard glow className="h-full flex flex-col">
      <div className="flex-1">
        {/* Category Badge */}
        <div className="mb-3">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${categoryColors[resource.category]}`}
          >
            {categoryIcons[resource.category]}
            {RESOURCE_CATEGORIES[resource.category]}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-lg font-display font-semibold text-text-primary mb-2">
          {resource.title}
        </h3>

        {/* Description */}
        <p className="text-text-secondary text-sm leading-relaxed">
          {resource.description}
        </p>
      </div>

      {/* Action Button */}
      <div className="mt-4 pt-4 border-t border-border-primary">
        <motion.a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-accent-primary hover:text-accent-secondary transition-colors text-sm font-medium"
          whileHover={{ x: 5 }}
        >
          <ExternalLink className="w-4 h-4" />
          Visit {resource.title}
        </motion.a>
      </div>
    </TechCard>
  );
}
